import { Effect } from '../../types/Animations/Effects';
import { PlayRanges } from '../../types/Segment/PlayRange';

/**
 * Maps effects from original timeline to playable timeline based on play ranges.
 * This is needed for composite segments that have trimmed/deleted sections (playRanges)
 * where effects need to be adjusted to the new playable timeline.
 *
 * @param effects - Array of effects with timings relative to original timeline
 * @param playRanges - Array of play ranges defining which parts of the original timeline are actually played
 * @param fps - Frames per second for time calculations
 * @returns Array of effects with timings adjusted to the playable timeline
 */
export function mapEffectsToPlayableTimeline(effects: Effect[] = [], playRanges: PlayRanges, fps: number): Effect[] {
  if (effects.length === 0) {
    return [];
  }

  // Helper function to map original time to playable time
  const mapOriginalToPlayableTime = (originalTime: number): number => {
    let currentPlayableTime = 0;

    // Walk through play ranges to build mapping
    for (const playRange of playRanges) {
      const rangeStartOriginal = playRange.fromFrame / fps;
      const rangeEndOriginal = playRange.toFrame / fps;
      const rangeDuration = rangeEndOriginal - rangeStartOriginal;

      // If our target original time falls within this play range
      if (originalTime >= rangeStartOriginal && originalTime <= rangeEndOriginal) {
        const offsetInRange = originalTime - rangeStartOriginal;
        return currentPlayableTime + offsetInRange;
      }

      // If our target time is before this range, it maps to current playable time
      if (originalTime < rangeStartOriginal) {
        return currentPlayableTime;
      }

      // Move past this play range
      currentPlayableTime += rangeDuration;
    }

    // If original time is after all play ranges, map to end of playable timeline
    return currentPlayableTime;
  };

  // Map each effect from original time to playable time
  return effects.map(effect => ({
    ...effect,
    start: mapOriginalToPlayableTime(effect.start),
    duration: mapOriginalToPlayableTime(effect.start + effect.duration) - mapOriginalToPlayableTime(effect.start)
  }));
}
